import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Clock, Crown, ArrowRight, Instagram, Star, Check, Navigation } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/blocks";
import { useUI } from "@/context/UIContext";

export default function CentreDetail() {
  const { slug } = useParams();
  const { openLead } = useUI();
  const [centre, setCentre] = useState(null);
  const [reals, setReals] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCentre(null); setNotFound(false);
    api.get(`/centres/${slug}`).then(({ data }) => setCentre(data)).catch(() => setNotFound(true));
    api.get("/realisations", { params: { centre: slug } }).then(({ data }) => setReals(data));
  }, [slug]);

  if (notFound) return <div className="pt-40 pb-24 text-center"><h1 className="font-display text-3xl uppercase">Centre introuvable</h1><Link to="/centres" className="hp-cta inline-block mt-6 px-6 py-3">Voir tous les centres</Link></div>;
  if (!centre) return <div className="pt-40 pb-40 text-center text-neutral-500">Chargement…</div>;

  return (
    <div data-testid="centre-detail-page">
      <Seo title={`${centre.name}${centre.is_mother ? " — Maison mère" : ""} | Reprogrammation moteur ${centre.city}`}
        description={`${centre.name} : reprogrammation moteur, Stage 1, Stage 2, conversion E85 et banc de puissance à ${centre.city}. ${centre.description.slice(0, 100)}`}
        canonical={`https://www.horsepowergarage.fr/centres/${slug}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "AutoRepair", name: centre.name,
          telephone: centre.phone, address: { "@type": "PostalAddress", streetAddress: centre.address, addressLocality: centre.city } }} />

      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end pt-20">
        <div className="absolute inset-0">
          <img src={centre.image} alt={centre.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full">
          {centre.is_mother && <p className="label-eyebrow mb-3 flex items-center gap-2"><Crown size={14} /> Maison mère du réseau</p>}
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl">{centre.name}</h1>
          {centre.tagline && <p className="text-neutral-300 text-lg mt-4">{centre.tagline}</p>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {centre.is_mother && (
            <Reveal>
              <div className="border-l-2 border-hp-yellow bg-hp-carbon p-6 mb-8">
                <p className="label-eyebrow mb-2 flex items-center gap-2"><Crown size={14} /> Centre historique</p>
                <p className="text-neutral-200 leading-relaxed">
                  Béziers est la maison mère du réseau Horsepower : c'est ici que naissent et sont validées nos méthodologies,
                  avant d'être déployées dans l'ensemble de nos centres.
                </p>
              </div>
            </Reveal>
          )}
          <Reveal>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl mb-4">Présentation</h2>
            <p className="text-neutral-400 leading-relaxed">{centre.description}</p>
          </Reveal>

          <Reveal delay={0.05}>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl mt-10 mb-4">Services disponibles</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {centre.services.map((s) => (
                <div key={s} className="flex items-center gap-3 hp-card p-4"><Check className="text-hp-yellow shrink-0" size={18} /><span className="text-neutral-200 text-sm">{s}</span></div>
              ))}
            </div>
          </Reveal>

          {reals.length > 0 && (
            <Reveal delay={0.05}>
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl mt-10 mb-4">Réalisations du centre</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {reals.map((r) => (
                  <div key={r.id} className="hp-card overflow-hidden">
                    <img src={r.image.startsWith("/photos/") ? r.image.replace(".webp", "-sm.webp") : r.image} alt={`${r.vehicle} — Horsepower ${centre.name}`} className="h-56 w-full object-cover" loading="lazy" />
                    <div className="p-4">
                      <h3 className="font-display font-bold uppercase text-sm">{r.vehicle}</h3>
                      {r.origin_hp && r.final_hp
                        ? <div className="flex items-center gap-3 mt-2 font-stat"><span className="text-neutral-400">{r.origin_hp} CH</span><ArrowRight size={14} className="text-hp-yellow" /><span className="text-hp-yellow font-black text-xl">{r.final_hp} CH</span></div>
                        : <p className="text-xs text-neutral-500 mt-1">{r.modification}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="hp-card p-6 lg:sticky lg:top-24">
            <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-5">Informations</h3>
            <ul className="space-y-4 text-sm text-neutral-300">
              {(centre.address || centre.postal_city) && <li className="flex items-start gap-3"><MapPin size={18} className="text-hp-yellow mt-0.5 shrink-0" /><span>{[centre.address, centre.postal_city].filter(Boolean).join(", ")}</span></li>}
              <li className="flex items-center gap-3"><Phone size={18} className="text-hp-yellow shrink-0" /><a href={`tel:${centre.phone.replace(/\s/g,"")}`} className="hover:text-hp-yellow">{centre.phone}</a></li>
              {centre.hours && <li className="flex items-start gap-3"><Clock size={18} className="text-hp-yellow mt-0.5 shrink-0" /><span>{centre.hours}</span></li>}
            </ul>
            <div className="flex flex-col gap-3 mt-6">
              <button onClick={() => openLead({ request_type: "rdv", centre_slug: centre.slug })} className="hp-cta px-5 py-3.5 text-sm" data-testid="centre-detail-rdv">Prendre rendez-vous</button>
              <button onClick={() => openLead({ request_type: "rappel", centre_slug: centre.slug })} className="hp-ghost px-5 py-3.5 text-sm" data-testid="centre-detail-rappel">Être rappelé</button>
              {centre.maps_url && <a href={centre.maps_url} target="_blank" rel="noopener noreferrer" className="hp-ghost px-5 py-3.5 text-sm inline-flex items-center justify-center gap-2"><Navigation size={15} /> Itinéraire</a>}
              {centre.instagram && <a href={centre.instagram} target="_blank" rel="noopener noreferrer" className="hp-ghost px-5 py-3.5 text-sm inline-flex items-center justify-center gap-2"><Instagram size={15} /> Instagram</a>}
            </div>
            {centre.google_rating && (
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2">
                <Star className="text-hp-yellow fill-hp-yellow" size={18} />
                <span className="font-stat font-bold text-xl">{centre.google_rating}</span>
                <span className="text-neutral-500 text-sm">({centre.google_reviews_count} avis Google)</span>
              </div>
            )}
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
